import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { IClient } from '../../core/models/clients.models';
import { ITask } from '../../core/models/tasks.models';
import { AddTaskModal } from '../../shared/modals/add-task/add-task.component';
import { AppService } from '../../store/app.service';
import { TasksApiService } from './tasks.apiservice';
import { Utility } from '../../shared/utility';

@Injectable()
export class TasksService {

    public activeClient: IClient = {} as IClient;
    public onSave: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public onDelete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public onMarkAsComplete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public onReloadTaskData: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public onBulkEditComplete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private modalService: NgbModal,
        private appService: AppService,
        private taskApi: TasksApiService
    ) { }

    openEditTaskModal(evt: any, canEdit = true, canDelete = true): NgbModalRef {
        const dialogRef = this.modalService.open(AddTaskModal);
        this.configureTaskModal(dialogRef, evt, canEdit, canDelete);
        const dialogSubmitSubscription = dialogRef.componentInstance.oSaveClicked.subscribe((r: ITask) => {
            this.taskApi.saveTask(r)
                .subscribe((response: any) => {
                    dialogRef.dismiss();
                    this.onSave.next(true);
                });
            dialogSubmitSubscription.unsubscribe();
        });
        const dialogDeleteSubscription = dialogRef.componentInstance.oDeleteClicked.subscribe((id: string) => {
            this.taskApi.deleteTask(id)
                .subscribe((response: any) => {
                    dialogRef.dismiss();
                    this.onDelete.next(true);
                })
            dialogDeleteSubscription.unsubscribe();
        });
        const dialogMarkSubscription = dialogRef.componentInstance.oMarkAsCompleteClicked.subscribe((r: any) => {
            this.taskApi.markTask(r)
                .subscribe((response: any) => {
                    dialogRef.dismiss();
                    this.onMarkAsComplete.next(true);
                })
            dialogMarkSubscription.unsubscribe();
        });
        return dialogRef; // Return the modal reference
    }

    openBulkEditTaskModal(tasks: Array<ITask>): NgbModalRef {
        const dialogRef = this.modalService.open(AddTaskModal);
        this.configureTaskModal(dialogRef, { id: null }, true, false);
        dialogRef.componentInstance.iIsBulkEdit = true;
        dialogRef.componentInstance.iBulkTasks = tasks;

        const bulkSaveSubscription = dialogRef.componentInstance.oBulkSaveClicked.subscribe((payload: { tasks: Array<ITask>, patch: any }) => {
            const updates = this.buildBulkTaskUpdates(payload.tasks ?? [], payload.patch ?? {});
            if (updates.length === 0) {
                dialogRef.dismiss();
                bulkSaveSubscription.unsubscribe();
                return;
            }

            forkJoin(updates.map(task => this.taskApi.saveTask(task)))
                .subscribe((response: any) => {
                    dialogRef.dismiss();
                    this.onBulkEditComplete.next(true);
                    this.onSave.next(true);
                });

            bulkSaveSubscription.unsubscribe();
        });

        return dialogRef;
    }

    private configureTaskModal(dialogRef: NgbModalRef, evt: any, canEdit: boolean, canDelete: boolean) {
        dialogRef.componentInstance.iId = evt.id;
        dialogRef.componentInstance.iCanEdit = canEdit;
        dialogRef.componentInstance.iCanDelete = canDelete;
        dialogRef.componentInstance.iClientId = evt.clientId ? evt.clientId : null;
        dialogRef.componentInstance.iCampaignId = evt.campaignId ? evt.campaignId : null;
    }

    private buildBulkTaskUpdates(tasks: Array<ITask>, patch: any): Array<ITask> {
        const updates: Array<ITask> = [];

        for (const task of tasks) {
            const updated = { ...task } as ITask;
            let changed = false;

            if (patch.assignedUserId !== undefined && patch.assignedUserId !== task.assignedUserId) {
                updated.assignedUserId = patch.assignedUserId;
                changed = true;
            }

            if (patch.dueDate !== undefined) {
                const currentDueDate = this.normalizeTaskDueDate(task.dueDate);
                const nextDueDate = this.normalizeTaskDueDate(patch.dueDate);
                if (currentDueDate !== nextDueDate) {
                    updated.dueDate = patch.dueDate;
                    changed = true;
                }
            }

            if (changed) {
                if (updated.dueDate) {
                    updated.dueDate = Utility.getTaskDueDateFromDateTime(new Date(updated.dueDate));
                }
                updates.push(updated);
            }
        }

        return updates;
    }

    private normalizeTaskDueDate(value: any): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return String(value);
        }

        return Utility.getTaskDueDateFromDateTime(date);
    }

    ReloadTaskData(evt: any){
        this.onReloadTaskData.next(true);
    }
}
